/* eslint jsx-a11y/no-autofocus: off */
import React, { useState } from 'react';
import { AnySchema } from 'yup';

import styles from '../Table.module.scss';

interface Props {
  value: string;
  options: { id: number; label: string }[];

  validationSchema: AnySchema;
  onValidationFailure: (error: string) => void;
  onConfirm: (value: any) => void;
}

const SearchCell = ({
  value: defaultValue,
  options,
  validationSchema,
  onConfirm,
  onValidationFailure,
}: Props) => {
  const [value, setValue] = useState(defaultValue);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [editMode, setEditMode] = useState(false);

  let submitted = false;

  const filteredOptions = options
    .filter(({ label }) => new RegExp(value, 'i').test(label))
    .slice(0, 5);

  const validateAndConfirm = async () => {
    setEditMode(false);
    if (submitted) return;
    submitted = true;

    try {
      await validationSchema?.validate(value);

      const selectedOption = filteredOptions.find(
        ({ label }) => label === value,
      );
      const selectedOptionId = selectedOption?.id ?? 0;
      const selectedOptionLabel = selectedOption?.label ?? '';

      setValue(selectedOptionLabel);
      onConfirm(selectedOptionId);
    } catch (error) {
      setValue(defaultValue);
      onValidationFailure?.(error.message);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value ?? '');
    setSelectedIndex(0);
  };

  const handleBlur = () => validateAndConfirm();

  const handleKeydown = (event: React.KeyboardEvent) => {
    if (event.code === 'Delete') event.stopPropagation();
    if (event.code === 'Enter') validateAndConfirm().then(() => {});
  };

  return (
    <td
      onDoubleClick={() => setEditMode(true)}
      style={{ position: 'relative' }}
    >
      {editMode ? (
        <>
          <input
            className={styles.table__cell_input}
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeydown}
            autoFocus
          />

          <div className={styles.table__cell_dropdown}>
            {filteredOptions.map(({ id, label }, index) => (
              <div
                key={label}
                role="presentation"
                className={`${styles.table__cell_dropdown_option} ${
                  index === selectedIndex
                    ? styles.table__cell_dropdown_option__selected
                    : ''
                }`}
                onMouseEnter={() => setSelectedIndex(index)}
                onMouseDown={(event) => {
                  event.stopPropagation();
                  setValue(label);
                }}
              >
                {label}
              </div>
            ))}
          </div>
        </>
      ) : (
        value
      )}
    </td>
  );
};

export default SearchCell;
